import type { ReactNode } from 'react';
import skyBackground from '../assets/sky-background.png';
import { BrandLogo } from './ui/brand-logo';
import { Mascot, type MascotPose } from './ui/mascot';

export interface AuthBrand {
  headline: string;
  subline: string;
  /** Two glass stat tiles under the headline. */
  tiles: { value: string; label: string }[];
  /** The customer testimonial card (client screens only). */
  testimonial?: boolean;
  mascot?: MascotPose;
}

/** Brand panel copy per audience, from the C01 (client) and A01 (staff) frames. */
export const CLIENT_BRAND: AuthBrand = {
  headline: 'Relax, Bhai.\nYour mate’s got this.',
  subline: 'Your business mate for visibility, marketing & growth.',
  tiles: [
    { value: '500+', label: 'Businesses growing' },
    { value: '24 hrs', label: 'Website go-live' },
  ],
  testimonial: true,
  mascot: 'wave',
};

export const STAFF_BRAND: AuthBrand = {
  headline: 'Your agency desk.\nEvery client, one place.',
  subline: 'Staff workspace for the MyBusinessMate team.',
  tiles: [
    { value: 'Staff', label: 'Invite-only accounts' },
    { value: 'Logged', label: 'Every sign-in is audited' },
  ],
  mascot: 'wave',
};

// TODO(api): the design personalises this panel from the invite ("Video · Department / Specialist", "5 max active
// tasks"). No endpoint returns invite details for a token yet, so the tiles are generic.
export const INVITE_BRAND: AuthBrand = {
  headline: 'Welcome to\nthe team.',
  subline: 'Your desk for client work, reviews and chat.',
  tiles: [
    { value: 'Review', label: 'Nothing goes live unchecked' },
    { value: 'Logged', label: 'Every sign-in is audited' },
  ],
  mascot: 'wave',
};

/**
 * Two-column auth layout from the C01 / A01 frames: a 640px brand panel (sky, headline, social proof,
 * mascot) beside a centred 400px form column. Below `lg` the panel is dropped and the logo moves above the form.
 */
export function AuthShell({ children, brand = CLIENT_BRAND }: { children: ReactNode; brand?: AuthBrand }) {
  return (
    <div className="min-h-dvh bg-surface-card p-(--mbm-space-4) lg:grid lg:grid-cols-[var(--mbm-layout-auth-panel-width)_minmax(0,1fr)]">
      <AuthBrandPanel brand={brand} />
      <main className="flex flex-col items-center justify-center px-(--mbm-space-4) py-(--mbm-space-8)">
        <BrandLogo className="mb-(--mbm-space-8) lg:hidden" />
        <div className="flex w-full max-w-(--mbm-layout-auth-form-width) flex-col gap-(--mbm-space-6)">{children}</div>
      </main>
    </div>
  );
}

const GLASS =
  'border border-surface-card bg-overlay-glass backdrop-blur-(--mbm-blur-glass) rounded-(--mbm-component-radius-card)';

function StatTile({ value, label }: { value: string; label: string }) {
  return (
    <div className={`flex flex-1 flex-col gap-(--mbm-space-0-5) p-(--mbm-space-4) ${GLASS}`}>
      <span className="text-heading-h1 text-text-primary">{value}</span>
      <span className="text-body-xsmall text-text-secondary">{label}</span>
    </div>
  );
}

function BeforeAfter({ tone, label, value }: { tone: 'before' | 'now'; label: string; value: string }) {
  return (
    <div
      className={
        tone === 'now'
          ? 'flex flex-1 flex-col rounded-(--mbm-radius-sm) bg-feedback-success-bg p-(--mbm-space-3)'
          : 'flex flex-1 flex-col rounded-(--mbm-radius-sm) bg-surface-muted p-(--mbm-space-3)'
      }
    >
      <span className="text-caption text-text-tertiary">{label}</span>
      <span
        className={tone === 'now' ? 'text-heading-h2 text-feedback-success-text' : 'text-heading-h2 text-text-tertiary'}
      >
        {value}
      </span>
      <span className="text-caption text-text-tertiary">enquiries / week</span>
    </div>
  );
}

function Testimonial() {
  return (
    <figure className={`flex flex-col gap-(--mbm-space-2) p-(--mbm-space-4) ${GLASS}`}>
      <blockquote className="text-label-large text-text-strong">“My shop is finally on page one of Google.”</blockquote>
      <figcaption className="flex items-center gap-(--mbm-space-1-5) text-caption text-text-tertiary">
        <span aria-label="5 out of 5 stars" className="text-feedback-warning">
          ★★★★★
        </span>
        Ravi · Banjara Hills
      </figcaption>
      <div className="flex gap-(--mbm-space-2)">
        <BeforeAfter tone="before" label="Before" value="3" />
        <BeforeAfter tone="now" label="Now" value="21" />
      </div>
    </figure>
  );
}

function AuthBrandPanel({ brand }: { brand: AuthBrand }) {
  return (
    <aside className="relative hidden h-[calc(100dvh-2*var(--mbm-space-4))] overflow-hidden rounded-(--mbm-radius-xl) bg-brand-primary-light lg:sticky lg:top-(--mbm-space-4) lg:flex lg:flex-col">
      <img src={skyBackground} alt="" aria-hidden className="absolute inset-0 size-full object-cover" />
      <div className="relative flex h-full flex-col p-(--mbm-space-8)">
        <span className="inline-flex self-start rounded-full bg-surface-card py-(--mbm-space-2) pl-(--mbm-space-3) pr-(--mbm-space-4)">
          <BrandLogo />
        </span>

        <div className="mt-(--mbm-space-9) flex flex-col gap-(--mbm-space-3)">
          <h2 className="whitespace-pre-line text-metric-l text-text-primary">{brand.headline}</h2>
          <p className="text-body-medium text-text-strong">{brand.subline}</p>
        </div>

        <div className="mt-auto flex items-end justify-between gap-(--mbm-space-6)">
          <div className="flex w-(--mbm-layout-auth-proof-width) flex-col gap-(--mbm-space-3)">
            <div className="flex gap-(--mbm-space-3)">
              {brand.tiles.map((tile) => (
                <StatTile key={tile.label} value={tile.value} label={tile.label} />
              ))}
            </div>
            {brand.testimonial ? <Testimonial /> : null}
          </div>
          <Mascot
            pose={brand.mascot ?? 'wave'}
            className="h-(--mbm-size-mascot-height) w-(--mbm-size-mascot-width) shrink-0"
          />
        </div>
      </div>
    </aside>
  );
}
